/**
 * @efacture/fiscal-core - Invoice Numbering Service
 *
 * Abstraction for invoice number generation and persistence.
 * DGI requires sequential invoice numbering without gaps.
 *
 * This interface allows implementations using different persistence backends:
 * - In-memory (testing/development)
 * - PostgreSQL sequences (production SaaS)
 * - SQLite (on-premise/offline)
 * - Redis atomic counters (high-concurrency scenarios)
 *
 * @module services/invoice-numbering
 */
import { SECEF_TIME_ZONE, zonedDateTimeParts } from "../jurisdictions/registry.js";
// ============================================================================
// In-Memory Implementation
// ============================================================================
/**
 * In-memory implementation for testing/development
 *
 * This implementation stores counters in memory and is not thread-safe
 * across multiple processes. Use only for:
 * - Unit tests
 * - Development environments
 * - Single-process applications
 *
 * For production, use a database-backed implementation.
 */
export class InMemoryInvoiceNumberProvider {
    /**
     * Counter storage: Map<"TYPE-YEAR", number>
     * Example key: "FV-2026"
     */
    counters = new Map();
    /**
     * Used numbers for gap validation: Map<"TYPE-YEAR", Set<number>>
     * Tracks all issued numbers to detect gaps
     */
    usedNumbers = new Map();
    /**
     * Creates a unique key for the counter map
     */
    getKey(type, fiscalYear) {
        return `${type}-${fiscalYear}`;
    }
    async getNextNumber(type, fiscalYear) {
        const key = this.getKey(type, fiscalYear);
        // Get current counter or initialize to 0
        const current = this.counters.get(key) ?? 0;
        const next = current + 1;
        // Update counter
        this.counters.set(key, next);
        // Track used number for gap validation
        const used = this.usedNumbers.get(key) ?? new Set();
        used.add(next);
        this.usedNumbers.set(key, used);
        return next;
    }
    async getCurrentNumber(type, fiscalYear) {
        const key = this.getKey(type, fiscalYear);
        return this.counters.get(key) ?? 0;
    }
    async validateNoGaps(type, fiscalYear) {
        const key = this.getKey(type, fiscalYear);
        const current = this.counters.get(key) ?? 0;
        // If no invoices issued, there are no gaps
        if (current === 0) {
            return true;
        }
        const used = this.usedNumbers.get(key) ?? new Set();
        // Check that all numbers from 1 to current are used
        for (let i = 1; i <= current; i++) {
            if (!used.has(i)) {
                return false;
            }
        }
        return true;
    }
    async reset(type, fiscalYear) {
        const key = this.getKey(type, fiscalYear);
        this.counters.delete(key);
        this.usedNumbers.delete(key);
    }
    async resetAll() {
        this.counters.clear();
        this.usedNumbers.clear();
    }
}
// ============================================================================
// Formatting Functions
// ============================================================================
/**
 * Formats an invoice number according to DGI spec
 *
 * Format: TYPE-YYYYMMDD-NNNNN
 *
 * Where:
 * - TYPE: Invoice type code (FV, FT, FA, EV, ET, EA)
 * - YYYYMMDD: The date of the issue instant at `SECEF_TIME_ZONE`, whatever the
 *   host's zone: the same calendar day as `getFiscalYear`
 * - NNNNN: Sequence number padded to 5 digits
 *
 * @param type - Invoice type code
 * @param date - Issue instant
 * @param sequenceNumber - Sequence number from the provider
 * @returns Formatted invoice number
 *
 * @example
 * ```typescript
 * formatInvoiceNumber("FV", new Date(Date.UTC(2026, 0, 18)), 1);
 * // Returns: "FV-20260118-00001"
 *
 * formatInvoiceNumber("FT", new Date(Date.UTC(2026, 5, 15)), 42);
 * // Returns: "FT-20260615-00042"
 *
 * formatInvoiceNumber("FA", new Date(Date.UTC(2026, 11, 31)), 99999);
 * // Returns: "FA-20261231-99999"
 * ```
 */
export function formatInvoiceNumber(type, date, sequenceNumber) {
    const { year, month, day } = zonedDateTimeParts(date, SECEF_TIME_ZONE);
    const sequence = String(sequenceNumber).padStart(5, "0");
    return `${type}-${year}${month}${day}-${sequence}`;
}
/**
 * Parses an invoice number into its components. `date` is the start of the
 * number's day at `SECEF_TIME_ZONE`, which every admitted profile keeps at
 * UTC+0 (`assertUtcZone`).
 *
 * @param invoiceNumber - Invoice number in format TYPE-YYYYMMDD-NNNNN
 * @returns Parsed components or null if invalid format
 *
 * @example
 * ```typescript
 * parseInvoiceNumber("FV-20260118-00001");
 * // Returns: { type: "FV", date: Date(2026-01-18), sequenceNumber: 1 }
 *
 * parseInvoiceNumber("invalid");
 * // Returns: null
 * ```
 */
export function parseInvoiceNumber(invoiceNumber) {
    const pattern = /^(FV|FT|FA|EV|ET|EA)-(\d{4})(\d{2})(\d{2})-(\d+)$/;
    const match = invoiceNumber.match(pattern);
    if (!match) {
        return null;
    }
    const [, type, year, month, day, sequence] = match;
    return {
        type: type,
        date: new Date(Date.UTC(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10))),
        sequenceNumber: parseInt(sequence, 10),
    };
}
/**
 * Extracts the fiscal year from a date.
 *
 * In Burkina Faso, the fiscal year matches the calendar year, and
 * Africa/Ouagadougou is UTC+0. Reads the UTC year, not the process's local
 * time zone, so it agrees with the accounting module (which derives its own
 * exercise years with `getUTCFullYear` throughout
 * `services/accounting/*.ts`) regardless of the host's `TZ`.
 *
 * @param date - Date to extract fiscal year from
 * @returns Fiscal year (e.g., 2026)
 */
export function getFiscalYear(date) {
    return date.getUTCFullYear();
}
/**
 * Validates that an invoice number matches the expected format
 *
 * @param invoiceNumber - Invoice number to validate
 * @returns true if valid format, false otherwise
 */
export function isValidInvoiceNumberFormat(invoiceNumber) {
    return parseInvoiceNumber(invoiceNumber) !== null;
}
//# sourceMappingURL=invoice-numbering.js.map